import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Layer } from 'src/app/core/models/layer';
import { TtlCacheStore } from '@/core/utils/cache/ttl-cache-store';
import { LayerRepository } from '@/core/repositories/layer/layer.repository';
import { LAYER_CACHE_TTL } from '@/core/config/cache-ttl';

@Injectable({ providedIn: 'root' })
export class LayerService {
    private readonly layerStore = new TtlCacheStore<Layer[]>('layers', [], LAYER_CACHE_TTL, () =>
        this.layerRepository.getAll(),
    );
    public layers$ = this.layerStore.state$;

    constructor(private layerRepository: LayerRepository) {}

    public loadLayers(forceLoad: boolean = false): Observable<Layer[]> {
        return this.layerStore.load(forceLoad);
    }

    /**
     * Unsafe method for getting the cached layers value. It returns the accounts layers.
     * Attention: This method may return undefined if the layers are not loaded yet.
     * @deprecated Use layers$ or getCurrentUser() instead.
     * @returns CurrentUser | undefined - The current user or undefined if not loaded yet.
     */
    getCachedLayers(): Layer[] | undefined {
        if (!this.layerStore.ready()) {
            console.error('LayerService.getCachedLayers() - Requesting cached layers before they are loaded!');
            return undefined;
        }
        return this.layerStore.get();
    }

    /**
     * Async alternative for getting the layers.
     * It delivers the same result as loadLayers() but returns a Promise.
     * Calling this method is in most cases fast as the layers object is cached.
     * @returns Observable<CurrentUser> - The current users
     */
    public async getLayers(): Promise<Layer[]> {
        return this.layerStore.getAsync();
    }

    public async getLayer1Id(): Promise<number> {
        const layers = await this.getLayers();
        const layerId = layers.find((layer) => layer.index === 1)?.id;
        return layerId as number; // this can only be undefined in case of a breaking account misconfiguration
    }

    public createIndexLayer(layer: Layer): Observable<void> {
        return this.layerRepository.createIndexLayer(layer).pipe(
            tap(() => {
                void this.layerStore.refresh();
            }),
        );
    }

    public update(layer: Layer): Observable<void> {
        return this.layerRepository.update(layer).pipe(
            tap(() => {
                void this.layerStore.refresh();
            }),
        );
    }

    public clearCache(): void {
        this.layerStore.reset();
    }
}
